<template>
  <div id="modalCreateReceita" class="modal fade" tabindex="-1">
    <div class="modal-dialog modal-lg">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">Cadastrar Receita</h5>
          <button
            type="button"
            class="btn-close"
            data-bs-dismiss="modal"
            title="Fechar"
            @click="$emit('closeModal', false)"
          />
        </div>
        <form @submit.prevent="save">
          <div class="modal-body">
            <div class="row g-3">
              <div class="col-sm-8">
                <label class="form-label">
                  Receita: <span class="text-danger">*</span>
                </label>
                <input
                  type="text"
                  class="form-control"
                  :class="{
                    'is-invalid': v$.receita.nome.$errors.length,
                  }"
                  placeholder="Nome da Receita"
                  v-model="v$.receita.nome.$model"
                />
                <span
                  v-if="v$.receita.nome.required.$invalid"
                  class="invalid-feedback"
                >
                  O campo é obrigatório
                </span>
              </div>
              <div v-if="periodoOrcamentoProposta" class="col-sm-4">
                <label class="form-label">
                  Valor Proposto: <span class="text-danger">*</span>
                </label>
                <div class="input-group">
                  <span class="input-group-text">R$</span>
                  <input
                    type="text"
                    class="form-control text-end"
                    :class="{
                      'is-invalid': v$.receita.proposta.$errors.length,
                    }"
                    v-model="v$.receita.proposta.$model"
                    v-maska="'#*'"
                  />
                  <span class="input-group-text">,00</span>
                  <span
                    v-if="v$.receita.proposta.required.$invalid"
                    class="invalid-feedback"
                  >
                    O campo é obrigatório
                  </span>
                </div>
              </div>
              <div v-if="periodoOrcamentoReformulacao" class="col-sm-4">
                <label class="form-label">
                  Valor Reformulado: <span class="text-danger">*</span>
                </label>
                <div class="input-group">
                  <span class="input-group-text">R$</span>
                  <input
                    type="text"
                    class="form-control text-end"
                    :class="{
                      'is-invalid': v$.receita.reformulacao.$errors.length,
                    }"
                    v-model="v$.receita.reformulacao.$model"
                    v-maska="'#*'"
                  />
                  <span class="input-group-text">,00</span>
                  <span
                    v-if="v$.receita.reformulacao.required.$invalid"
                    class="invalid-feedback"
                  >
                    O campo é obrigatório
                  </span>
                </div>
              </div>
              <div class="col-sm-12">
                <label class="form-label">
                  Memória de Cálculo: <span class="text-danger">*</span>
                </label>
                <textarea
                  class="form-control"
                  :class="{
                    'is-invalid': v$.receita.descricao.$errors.length,
                  }"
                  rows="8"
                  v-model="v$.receita.descricao.$model"
                />
                <span
                  v-if="v$.receita.descricao.required.$invalid"
                  class="invalid-feedback"
                >
                  O campo é obrigatório
                </span>
              </div>
            </div>
          </div>
          <div class="modal-footer border-top-0 pt-0">
            <button
              type="button"
              class="btn btn-link"
              data-bs-dismiss="modal"
              title="Cancelar"
              @click="$emit('closeModal', false)"
            >
              Cancelar
            </button>
            <button
              type="submit"
              class="btn btn-success"
              title="Cadastrar"
              :disabled="v$.$invalid || disableButton"
            >
              <span v-if="disableButton">
                <i class="ph-spinner spinner" />
              </span>
              <span v-else>Cadastrar</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { useVuelidate } from '@vuelidate/core'
import { required, requiredIf, numeric } from '@vuelidate/validators'
import { maska } from 'maska'
import { showModal, hideModal } from '@/utils/modal'
import PeriodoOrcamentoEnum from '@/enum/periodoOrcamentoEnum'

export default {
  name: 'CreateReceitaModal',

  props: {
    codCCustoPai: {
      type: String,
      required: true,
    },
  },

  setup() {
    return { v$: useVuelidate() }
  },

  directives: { maska },

  data() {
    return {
      receita: {
        codColigada: 1,
        nome: '',
        proposta: '',
        reformulacao: '',
        descricao: '',
      },
      disableButton: false,
    }
  },

  computed: {
    ...mapGetters(['orcamentoSelecionado']),

    periodoOrcamentoProposta() {
      return (
        this.orcamentoSelecionado.periodoOrcamento ===
        PeriodoOrcamentoEnum.PROPOSTA
      )
    },

    periodoOrcamentoReformulacao() {
      return (
        this.orcamentoSelecionado.periodoOrcamento ===
        PeriodoOrcamentoEnum.REFORMULACAO
      )
    },
  },

  methods: {
    async save() {
      if (!this.v$.$invalid) {
        this.disableButton = true
        try {
          const response = await this.$store.dispatch('saveReceita', {
            codCCustoPai: this.codCCustoPai,
            orcamentoId: this.orcamentoSelecionado.id,
            receita: this.receita,
          })

          hideModal()
          this.$emit('closeModal', true)
          this.$root.$refs.notification.success(response.message)
        } catch (error) {
          this.disableButton = false
          if (error && error.message) {
            this.$root.$refs.notification.warning(error.message)
          } else {
            this.$root.$refs.notification.error(
              'Não foi possível se conectar ao servidor.',
            )
          }
        }
      }
    },
  },

  validations() {
    return {
      receita: {
        nome: {
          required,
        },
        proposta: {
          required: requiredIf(() => {
            if (
              this.orcamentoSelecionado.periodoOrcamento ===
              PeriodoOrcamentoEnum.PROPOSTA
            ) {
              return true
            }
            return false
          }),
          numeric,
        },
        reformulacao: {
          required: requiredIf(() => {
            if (
              this.orcamentoSelecionado.periodoOrcamento ===
              PeriodoOrcamentoEnum.REFORMULACAO
            ) {
              return true
            }
            return false
          }),
          numeric,
        },
        descricao: {
          required,
        },
      },
    }
  },

  mounted() {
    showModal('modalCreateReceita')
  },
}
</script>
